import type { ComponentType, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { BookingButton } from '@/components/booking/BookingButton'
import { Container } from '@/components/ui/Container'
import {
  FacebookIcon,
  InstagramIcon,
  TiktokIcon,
  YelpIcon,
} from '@/components/icons/SocialIcons'
import { site } from '@/content/site'
import { telHref } from '@/lib/tel'
import { cn } from '@/lib/utils'

const exploreLinks = [
  { to: '/gallery', label: 'Gallery' },
  { to: '/location', label: 'Visit us' },
  { to: '/faq', label: 'FAQ' },
] as const

type SocialLink = {
  href: string
  label: string
  Icon: ComponentType<{ className?: string }>
}

const socialLinks: readonly SocialLink[] = [
  { href: site.social.instagram, label: 'Instagram', Icon: InstagramIcon },
  { href: site.social.facebook, label: 'Facebook', Icon: FacebookIcon },
  { href: site.social.tiktok, label: 'TikTok', Icon: TiktokIcon },
  { href: site.social.yelp, label: 'Yelp', Icon: YelpIcon },
]

const HEADING_CLS =
  'mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-on-olive-muted'

/** Tiny underline-on-hover affordance shared by every footer link. */
function Underlined({ children }: { children: ReactNode }) {
  return (
    <span className="relative">
      {children}
      <span
        aria-hidden
        className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-on-olive transition-transform duration-300 ease-out group-hover:scale-x-100"
      />
    </span>
  )
}

export function Footer() {
  return (
    <footer className="border-t border-olive-border bg-olive pb-[var(--sticky-book-bar-height)] text-on-olive-soft min-[900px]:pb-0">
      <Container>
        <div className="grid gap-10 py-12 md:grid-cols-3 md:gap-8 md:py-[var(--spacing-section)] lg:grid-cols-12 lg:gap-12">
          {/* Brand */}
          <div className="md:col-span-3 lg:col-span-4">
            <Link
              to="/"
              className="inline-block font-display text-xl font-semibold tracking-tight text-on-olive"
            >
              {site.name}
            </Link>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-on-olive-soft">
              Nail care in {site.city}, {site.region}. Book now online or walk in when
              you are nearby.
            </p>
            <ul className="mt-6 flex flex-wrap items-center gap-0.5">
              {socialLinks.map(({ href, label, Icon }) => (
                <li key={label}>
                  <a
                    href={href}
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label={label}
                    className={cn(
                      'inline-flex h-11 w-11 items-center justify-start text-on-olive-soft opacity-90 transition-opacity hover:text-on-olive hover:opacity-100',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                    )}
                  >
                    <Icon />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2">
            <p className={HEADING_CLS}>Explore</p>
            <ul className="space-y-2.5">
              {exploreLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="group inline-flex text-sm text-on-olive-soft transition-colors hover:text-on-olive"
                  >
                    <Underlined>{l.label}</Underlined>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Visit */}
          <div className="lg:col-span-3">
            <p className={HEADING_CLS}>Visit us</p>
            <address className="text-sm not-italic leading-relaxed text-on-olive-soft">
              <a
                href={site.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex text-on-olive-soft transition-colors hover:text-on-olive focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-olive"
                aria-label={`Open ${site.formattedAddress} in Google Maps`}
              >
                <Underlined>
                  <>
                    {site.streetAddress}
                    <br />
                    {site.addressLocality}, {site.addressRegion} {site.postalCode}
                  </>
                </Underlined>
              </a>
            </address>
            <ul className="mt-3 space-y-1 text-sm text-on-olive-muted">
              {site.hours.map((h) => (
                <li key={h.day} className="flex flex-wrap gap-x-2">
                  <span className="text-on-olive">{h.day}</span>
                  <span>{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <p className={HEADING_CLS}>Contact</p>
            <ul className="space-y-2.5 text-sm">
              <li>
                <a
                  href={telHref(site.phone)}
                  className="group inline-flex text-on-olive transition-colors hover:text-canvas"
                >
                  <Underlined>{site.phone}</Underlined>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${site.email}`}
                  className="group inline-flex break-all text-on-olive transition-colors hover:text-canvas"
                >
                  <Underlined>{site.email}</Underlined>
                </a>
              </li>
            </ul>
            <div className="mt-5 hidden min-[900px]:block">
              <BookingButton variant="onDark" className="min-h-11" />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className={cn(
            'flex flex-col-reverse items-start gap-2 border-t border-olive-border py-6 text-xs text-on-olive-muted',
            'sm:flex-row sm:items-center sm:justify-between'
          )}
        >
          <p>
            © {new Date().getFullYear()} {site.name} LLC. All rights reserved.
          </p>
          <p>{site.formattedAddress}</p>
        </div>
      </Container>
    </footer>
  )
}
